import { DrivePickerButton } from "@components/shared/DrivePickerButton";
import { FileDropZone } from "@components/shared/FileDropZone";
import { UPDATE_ORGANISATION_BRANDING } from "@lib/graphql/mutations";
import { ORGANISATION_SETTINGS_QUERY } from "@lib/graphql/queries";
import { Trash2, Upload } from "lucide-react";
import { useState } from "react";
import { useMutation, useQuery } from "urql";

interface BrandingForm {
  primary_color: string;
  secondary_color: string;
  accent_color: string;
  heading_font: string;
  body_font: string;
}

const FONT_OPTIONS = [
  "",
  "Inter",
  "Roboto",
  "Open Sans",
  "Lato",
  "Montserrat",
  "Poppins",
  "Source Sans Pro",
  "Merriweather",
  "Playfair Display",
  "Georgia",
  "Times New Roman",
];

function brandingToForm(branding: Record<string, string>): BrandingForm {
  return {
    primary_color: branding.primary_color || "",
    secondary_color: branding.secondary_color || "",
    accent_color: branding.accent_color || "",
    heading_font: branding.heading_font || "",
    body_font: branding.body_font || "",
  };
}

export function BrandingSettings() {
  const [{ data }, reexecute] = useQuery({ query: ORGANISATION_SETTINGS_QUERY });
  const [, updateBranding] = useMutation(UPDATE_ORGANISATION_BRANDING);

  const org = data?.currentOrganisation;
  const isAdmin = org?.currentUserRole === "admin";
  const branding = (org?.branding || {}) as Record<string, string>;
  const logoUrl = org?.logoUrl as string | null;

  const [form, setForm] = useState<BrandingForm>(brandingToForm(branding));
  const [dirty, setDirty] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [removingLogo, setRemovingLogo] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [success, setSuccess] = useState(false);

  // Sync form state when data loads (matching AiSettings pattern)
  const savedForm = brandingToForm(branding);
  if (!dirty && JSON.stringify(form) !== JSON.stringify(savedForm)) {
    setForm(savedForm);
  }

  function handleChange(field: keyof BrandingForm, value: string) {
    setForm((prev) => ({ ...prev, [field]: value }));
    setDirty(true);
    setSuccess(false);
  }

  async function handleLogoUpload(file: File) {
    setUploading(true);
    setErrors([]);
    try {
      const formData = new FormData();
      formData.append("file", file);

      const csrfToken = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content;
      const response = await fetch("/uploads", {
        method: "POST",
        headers: csrfToken ? { "X-CSRF-Token": csrfToken } : {},
        body: formData,
      });

      const uploadData = await response.json();
      await attachLogoBySignedId(uploadData.signed_id);
    } finally {
      setUploading(false);
    }
  }

  async function attachLogoBySignedId(signedId: string) {
    const result = await updateBranding({
      input: { logoSignedId: signedId },
    });
    const mutationErrors = result.data?.updateOrganisationBranding?.errors || [];
    if (mutationErrors.length === 0) {
      reexecute({ requestPolicy: "network-only" });
    } else {
      setErrors(mutationErrors);
    }
  }

  async function handleRemoveLogo() {
    setRemovingLogo(true);
    setErrors([]);
    const result = await updateBranding({
      input: { removeLogo: true },
    });

    const mutationErrors = result.data?.updateOrganisationBranding?.errors || [];
    if (mutationErrors.length === 0) {
      reexecute({ requestPolicy: "network-only" });
    } else {
      setErrors(mutationErrors);
    }
    setRemovingLogo(false);
  }

  async function handleSave() {
    setSaving(true);
    setErrors([]);
    setSuccess(false);

    const result = await updateBranding({
      input: { branding: form },
    });

    const mutationErrors = result.data?.updateOrganisationBranding?.errors || [];
    if (mutationErrors.length === 0) {
      setDirty(false);
      setSuccess(true);
      reexecute({ requestPolicy: "network-only" });
    } else {
      setErrors(mutationErrors);
    }
    setSaving(false);
  }

  return (
    <div className="mt-6 space-y-8">
      {/* Logo */}
      <div>
        <h2 className="text-lg font-semibold text-text-primary">Logo</h2>
        <p className="mt-1 text-sm text-text-muted">
          Your logo appears on reports and client-facing portals.
        </p>
        <div className="mt-4 flex items-center gap-6">
          <div className="flex h-20 w-20 items-center justify-center overflow-hidden rounded-lg border border-border bg-card">
            {logoUrl ? (
              <img src={logoUrl} alt="Organisation logo" className="h-full w-full object-contain" />
            ) : (
              <span className="text-xs text-text-faint">No logo</span>
            )}
          </div>
          {isAdmin && (
            <div className="flex items-center gap-2">
              <FileDropZone
                accept="image/png,image/jpeg,image/svg+xml,image/webp"
                disabled={uploading}
                onFiles={(files) => handleLogoUpload(files[0])}
                overlayLabel="Drop logo here"
              >
                {({ openPicker }) => (
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={openPicker}
                      disabled={uploading}
                      className="flex cursor-pointer items-center gap-2 rounded-md border border-input-border px-4 py-2 text-sm font-medium text-text-secondary hover:bg-card-hover disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <Upload size={16} />
                      {uploading ? "Uploading..." : "Upload Logo"}
                    </button>
                    <DrivePickerButton
                      accept="image/png,image/jpeg,image/svg+xml,image/webp"
                      disabled={uploading}
                      onSignedId={(signedId) => attachLogoBySignedId(signedId)}
                    />
                  </div>
                )}
              </FileDropZone>
              {logoUrl && (
                <button
                  onClick={handleRemoveLogo}
                  disabled={removingLogo}
                  className="flex items-center gap-2 rounded-md border border-input-border px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20 disabled:opacity-50"
                >
                  <Trash2 size={16} />
                  {removingLogo ? "Removing..." : "Remove"}
                </button>
              )}
              <p className="text-xs text-text-faint">PNG, JPG, SVG, or WebP. Max 2MB.</p>
            </div>
          )}
        </div>
      </div>

      {/* Colors */}
      <div>
        <h2 className="text-lg font-semibold text-text-primary">Brand Colors</h2>
        <p className="mt-1 text-sm text-text-muted">
          Used in generated reports and client portals.
        </p>
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
          {(
            [
              ["primary_color", "Primary Color"],
              ["secondary_color", "Secondary Color"],
              ["accent_color", "Accent Color"],
            ] as const
          ).map(([field, label]) => (
            <div key={field}>
              <label className="block text-sm font-medium text-text-secondary">{label}</label>
              <div className="mt-1 flex items-center gap-2">
                <input
                  type="color"
                  value={form[field] || "#000000"}
                  onChange={(e) => handleChange(field, e.target.value)}
                  disabled={!isAdmin}
                  className="h-9 w-9 cursor-pointer rounded border border-input-border"
                />
                <input
                  type="text"
                  value={form[field]}
                  onChange={(e) => handleChange(field, e.target.value)}
                  disabled={!isAdmin}
                  placeholder="#000000"
                  className="block w-full rounded-md border border-input-border bg-card px-3 py-2 text-sm text-text-primary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent disabled:opacity-50"
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Fonts */}
      <div>
        <h2 className="text-lg font-semibold text-text-primary">Fonts</h2>
        <p className="mt-1 text-sm text-text-muted">Font families for generated documents.</p>
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
          {(
            [
              ["heading_font", "Heading Font"],
              ["body_font", "Body Font"],
            ] as const
          ).map(([field, label]) => (
            <div key={field}>
              <label className="block text-sm font-medium text-text-secondary">{label}</label>
              <select
                value={form[field]}
                onChange={(e) => handleChange(field, e.target.value)}
                disabled={!isAdmin}
                className="mt-1 block w-full rounded-md border border-input-border bg-card px-3 py-2 text-sm text-text-primary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent disabled:opacity-50"
              >
                <option value="">Default</option>
                {FONT_OPTIONS.filter(Boolean).map((font) => (
                  <option key={font} value={font}>
                    {font}
                  </option>
                ))}
              </select>
            </div>
          ))}
        </div>
      </div>

      {/* Errors / Success */}
      {errors.length > 0 && (
        <div className="rounded-md bg-red-50 p-3 dark:bg-red-900/20">
          {errors.map((error) => (
            <p key={error} className="text-sm text-red-700 dark:text-red-400">
              {error}
            </p>
          ))}
        </div>
      )}
      {success && (
        <p className="text-sm text-green-600 dark:text-green-400">Branding settings saved.</p>
      )}

      {/* Save */}
      {isAdmin && (
        <button
          onClick={handleSave}
          disabled={saving || !dirty}
          className="rounded-md bg-accent px-4 py-2 text-sm font-medium text-white hover:bg-accent-hover disabled:opacity-50"
        >
          {saving ? "Saving..." : "Save Branding"}
        </button>
      )}
    </div>
  );
}
